import { useState, useEffect } from 'react';
import { BRAND } from '../config/brand';
import { api } from '../api';

const G = BRAND.colors.primary;

// ShippingEstimator (June 2026, Push B redesign):
//
// Used by Presentations.jsx — opened per-product (each product ships from
// its own supplier/origin with its own carton data, so shipping is necessarily
// per-product, not per-order).
//
// Flow:
//   1. User enters destination zip + qty → "Get rates" calls the estimator API.
//   2. User picks a rate (or skips and types a manual amount).
//   3. The "Apply" panel appears with markup editor + three buttons:
//       - Add as line item   → mode='line'
//       - Blend per-unit     → mode='blended' (blendQty = the qty used in calc)
//       - Skip               → close without applying
//   4. onApply({ amount, mode, blendQty, label }) fires; parent stores
//      per-product in shippingMap and persists it inside the product item.
//
// Props:
//   product:   product object (used for rate request + carton hints)
//   qty:       current quantity of this product on the presentation
//   getToken:  Clerk token getter
//   existing:  pre-existing shipping config for this product (for editing)
//   onApply:   ({ amount, mode, blendQty, label }) => void
//   onCancel:  () => void  (closes the estimator without applying)
export default function ShippingEstimator({ product, qty: initialQty, getToken, existing, onApply, onCancel }) {
  const [destZip,  setDestZip]  = useState('');
  const [qty,      setQty]      = useState(initialQty || product?.min_qty || 100);
  const [results,  setResults]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [pickedRate, setPickedRate] = useState(null);
  const [markup,   setMarkup]   = useState(existing?.amount ? String(existing.amount) : '');
  const [manualMode, setManualMode] = useState(!!existing); // jump straight to manual entry when editing

  // If user is editing existing shipping, jump them right to the amount editor
  // (no need to re-run the calculator — they can if they want by clearing).
  useEffect(() => {
    if (existing?.amount) setMarkup(String(existing.amount));
  }, [existing]);

  const hasCartonData = product?.carton_qty && product?.carton_weight;
  const cartonCount   = hasCartonData ? Math.ceil(qty / product.carton_qty) : null;
  const totalWeight   = hasCartonData ? cartonCount * product.carton_weight : null;

  const estimate = async () => {
    if (destZip.length < 5) return;
    setLoading(true); setError(null); setResults(null);
    try {
      const token = await getToken();
      const data = await api.withToken(token).estimateShipping({ product_id:product.id, qty:+qty, dest_zip:destZip });
      setResults(data);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // The amount that will actually be applied. Prefers the user's markup field
  // (which auto-fills from the picked rate but can be edited up/down).
  const effectiveAmount = () => {
    const m = parseFloat(markup);
    if (Number.isFinite(m) && m > 0) return +m.toFixed(2);
    if (pickedRate?.price) return +Number(pickedRate.price).toFixed(2);
    return null;
  };

  const applyAs = (mode) => {
    const amt = effectiveAmount();
    if (!amt) { setError('Enter a shipping amount.'); return; }
    onApply({
      amount:    amt,
      mode,
      blendQty:  mode === 'blended' ? (qty || 1) : null,
      label:     pickedRate?.name?.replace(/ \(est\.\)$/, '') || existing?.label || (mode === 'blended' ? 'Shipping (blended)' : 'Shipping'),
    });
  };

  // Whether to show the apply panel — once user has either picked a rate
  // OR is in manual-entry mode AND has typed an amount.
  const showApplyPanel = pickedRate || manualMode;

  return (
    <div style={{ background:'#F8FAFC', borderRadius:10, border:'1px solid #E2E8F0', padding:14, marginTop:12 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
        📦 Shipping Estimate
        {hasCartonData
          ? <span style={{ background:`${G}18`, color:'#059669', fontSize:10, fontWeight:600, padding:'1px 7px', borderRadius:10 }}>✓ Real carton data</span>
          : <span style={{ fontSize:10, color:'#94A3B8', fontWeight:400 }}>rough estimate</span>
        }
      </div>

      {/* Calculator inputs — shown unless we're in pure manual mode and skipped the calc */}
      {!manualMode && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
            <div>
              <div style={{ fontSize:11, color:'#374151', marginBottom:4, fontWeight:500 }}>Destination zip</div>
              <input value={destZip} onChange={e=>setDestZip(e.target.value.replace(/\D/,'').slice(0,5))}
                onKeyDown={e=>e.key==='Enter'&&estimate()} placeholder="Enter zip code" maxLength={5}
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:6,padding:'6px 10px',fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
            </div>
            <div>
              <div style={{ fontSize:11, color:'#374151', marginBottom:4, fontWeight:500 }}>Quantity</div>
              <input type="number" min="1" value={qty} onChange={e=>setQty(Math.max(1,+e.target.value || 1))}
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:6,padding:'6px 10px',fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
            </div>
          </div>
          {hasCartonData && (
            <div style={{ fontSize:11, color:'#64748B', marginBottom:10 }}>
              ~{cartonCount} carton{cartonCount === 1 ? '' : 's'}, {totalWeight} lb total
            </div>
          )}
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button onClick={estimate} disabled={loading||destZip.length<5}
              style={{ background:destZip.length<5?'#E2E8F0':G, border:'none', borderRadius:7,
                padding:'7px 14px', color:destZip.length<5?'#94A3B8':'#fff',
                fontSize:12, fontWeight:500, cursor:destZip.length<5?'not-allowed':'pointer', fontFamily:'inherit' }}>
              {loading?'Getting rates...':'→ Get Rates'}
            </button>
            <button onClick={() => { setManualMode(true); setResults(null); setPickedRate(null); }}
              style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'7px 12px',
                fontSize:11,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
              Enter manually
            </button>
            <button onClick={onCancel}
              style={{ marginLeft:'auto',background:'none',border:'none',color:'#64748B',
                fontSize:11,cursor:'pointer',fontFamily:'inherit' }}>
              Cancel
            </button>
          </div>
          {error && <div style={{ marginTop:8, padding:'7px 10px', background:'#FEF2F2', borderRadius:6, fontSize:12, color:'#DC2626' }}>{error}</div>}
          {results?.rates?.length>0 && (
            <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:6 }}>
              {results.warning && <div style={{ fontSize:11, color:'#94A3B8', fontStyle:'italic' }}>⚠ {results.warning}</div>}
              {results.rates.map((r,i)=>{
                const isPicked = pickedRate && pickedRate.carrier === r.carrier && pickedRate.service === r.service;
                return (
                  <button key={i} onClick={() => { setPickedRate(r); setMarkup(String(r.price.toFixed(2))); setError(null); }}
                    style={{ background: isPicked ? `${G}18` : '#fff',
                      border:`1px solid ${isPicked ? G : '#E2E8F0'}`,
                      borderRadius:8, padding:'9px 12px',
                      display:'flex', justifyContent:'space-between', alignItems:'center',
                      gap:10, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight: isPicked ? 600 : 400, color:'#0F172A' }}>
                        {r.name}{r.estimated && <span style={{ fontSize:10, color:'#94A3B8' }}> (est.)</span>}
                      </div>
                      {r.deliveryDate && <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>Est. delivery: {r.deliveryDate}</div>}
                      {!r.deliveryDate && r.transitDays && <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>{r.transitDays} business day{r.transitDays === 1 ? '' : 's'}</div>}
                    </div>
                    <div style={{ fontSize:15, fontWeight:600, color: isPicked ? G : '#374151' }}>
                      ${r.price.toFixed(2)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {results?.rates?.length === 0 && (
            <div style={{ marginTop:10, fontSize:11, color:'#94A3B8', fontStyle:'italic' }}>
              No rates returned. Try entering manually instead.
            </div>
          )}
        </>
      )}

      {/* Apply panel — appears after picking a rate OR when manual */}
      {showApplyPanel && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E2E8F0' }}>
          <div style={{ fontSize:11,color:'#374151',marginBottom:6,fontWeight:500 }}>
            Amount to charge client {pickedRate && <span style={{ color:'#94A3B8',fontWeight:400 }}>(estimated ${pickedRate.price.toFixed(2)} — edit to add markup)</span>}
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:10 }}>
            <span style={{ fontSize:13,color:'#94A3B8' }}>$</span>
            <input type="number" min="0" step="0.01" value={markup}
              onChange={e => setMarkup(e.target.value)}
              placeholder="100.00"
              style={{ flex:1,border:'1px solid #E2E8F0',borderRadius:6,padding:'6px 10px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff' }}/>
          </div>
          <div style={{ fontSize:11,color:'#94A3B8',marginBottom:10,lineHeight:1.5 }}>
            <strong style={{ color:'#475569',fontWeight:600 }}>Add as line:</strong> shows as a separate "Shipping" line on the client's quote.{' '}
            <strong style={{ color:'#475569',fontWeight:600 }}>Blend:</strong> divides across {qty} units (${qty ? ((parseFloat(markup)||0)/qty).toFixed(2) : '0.00'}/unit) added to every price tier.
          </div>
          {error && <div style={{ marginBottom:10, padding:'6px 10px', background:'#FEF2F2', borderRadius:6, fontSize:11, color:'#DC2626' }}>{error}</div>}
          <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
            <button onClick={() => applyAs('line')}
              style={{ flex:1,background:G,border:'none',borderRadius:7,padding:'9px 12px',
                fontSize:12,color:'#fff',fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
              Add as line item
            </button>
            <button onClick={() => applyAs('blended')}
              style={{ flex:1,background:'#fff',border:`1px solid ${G}`,borderRadius:7,padding:'9px 12px',
                fontSize:12,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
              Blend per-unit
            </button>
            <button onClick={onCancel}
              style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 12px',
                fontSize:12,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
